/**
 * Phase 45 Plan 45-06 (D-10/D-11) — priority dot + optional label.
 *
 * Inline-style palette per UI-SPEC §Surface 2 Row grid spec:
 *   urgent → #EF4444 (rose-500)
 *   high   → #FC8332 (ms-orange)
 *   normal → #8E8E98 (warm gray)
 *   low    → #D9D2C4 (cream)
 *
 * Used both as a bare dot (col 1 in MyTaskRow) and as a labelled badge
 * (col 5 in UnassignedRow). Title attribute exposes the label to assistive
 * tech when dot-only (parent rows treat the dot as aria-hidden via
 * MyTaskRow's structure).
 */
import type { CSSProperties } from 'react';
import type { MyTaskDto } from '../../services/api/tasks';

export type Priority = MyTaskDto['priority'];

interface PriorityConfig {
  label: string;
  dot: string;
  bg: string;
  fg: string;
}

const PRIORITY_CFG: Record<string, PriorityConfig> = {
  urgent: { label: 'Urgent', dot: '#EF4444', bg: '#FEE2E2', fg: '#991B1B' },
  high: { label: 'High', dot: '#FC8332', bg: '#FFF1E5', fg: '#9A3F0B' },
  normal: { label: 'Normal', dot: '#8E8E98', bg: '#F4F1EC', fg: '#5C5C68' },
  low: { label: 'Low', dot: '#D9D2C4', bg: '#F4F1EC', fg: '#8E8E98' },
};

function resolveConfig(priority: Priority): PriorityConfig {
  return PRIORITY_CFG[priority] ?? PRIORITY_CFG.normal!;
}

export function priorityConfig(priority: Priority): PriorityConfig {
  return resolveConfig(priority);
}

export interface MtPriorityBadgeProps {
  priority: Priority;
  withLabel?: boolean;
  className?: string;
}

export function MtPriorityBadge({
  priority,
  withLabel = false,
  className = '',
}: MtPriorityBadgeProps) {
  const cfg = resolveConfig(priority);

  if (!withLabel) {
    const dotStyle: CSSProperties = { background: cfg.dot };
    return (
      <span
        title={cfg.label}
        aria-hidden="true"
        className={`w-2 h-2 rounded-full shrink-0 inline-block ${className}`}
        style={dotStyle}
        data-testid="mt-priority-dot"
      />
    );
  }

  const labelStyle: CSSProperties = { background: cfg.bg, color: cfg.fg };
  const dotStyle: CSSProperties = { background: cfg.dot };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider ${className}`}
      style={labelStyle}
      data-testid="mt-priority-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={dotStyle} />
      {cfg.label}
    </span>
  );
}
